<template>
  <div class="services-page">
    <section class="page-hero">
      <img src="/img/hero-project-inspection.webp" alt="对公工程查验与住宅项目质量过程管控服务" />
      <div class="page-hero-mask"></div>
      <div class="page-hero-content">
        <nav class="breadcrumb" aria-label="面包屑">
          <router-link to="/">首页</router-link>
          <span>/</span>
          <span>对公服务</span>
        </nav>
        <p class="eyebrow">Public Project Inspection</p>
        <h1>对公工程查验服务</h1>
        <p>
          面向开发商、建设单位、物业单位、工程项目和住宅交付项目，提供精装入场查验、过程质量巡检、分户查验、一房一验、交付前预验收、整改复查和工程质量检测服务。
        </p>
        <router-link class="btn" :to="{ path: '/', hash: '#lead-form' }">咨询项目查验方案</router-link>
      </div>
    </section>

    <section class="section intro-section">
      <div class="section-inner intro-layout">
        <div>
          <p class="eyebrow">Positioning</p>
          <h2>不是只在交付当天验房，而是参与项目质量过程管控</h2>
          <p>
            对公服务的重点，是在住宅项目精装入场、施工过程、分户查验、交付前预验收和整改复查等节点，为项目方提供第三方查验记录、问题清单、阶段汇总和闭环复核支持。
          </p>
        </div>
        <div class="highlight-grid">
          <article v-for="item in highlights" :key="item.title">
            <strong>{{ item.title }}</strong>
            <span>{{ item.desc }}</span>
          </article>
        </div>
      </div>
    </section>

    <section class="section muted">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">Service Modules</p>
          <h2>对公服务模块</h2>
          <p>根据项目阶段和现场目标，可单项服务，也可组合成覆盖“入场、过程、交付、整改”的服务包。</p>
        </div>
        <div class="service-grid">
          <article class="service-card" v-for="service in services" :key="service.slug">
            <img :src="service.image" :alt="service.shortTitle + '对公服务场景示意图'" />
            <div>
              <span>{{ service.serviceType }}</span>
              <h3>{{ service.shortTitle }}</h3>
              <p><strong>适用对象：</strong>{{ service.targetUsers.join("、") }}</p>
              <p>{{ service.summary }}</p>
              <router-link :to="service.path">查看详情</router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="section-inner two-column">
        <div>
          <p class="eyebrow">Customers</p>
          <h2>适用对象</h2>
          <p>服务面向住宅交付项目的工程管理、质量管理、物业前介、客户交付和整改复查场景。</p>
        </div>
        <div class="tag-grid">
          <span v-for="item in customers" :key="item">{{ item }}</span>
        </div>
      </div>
    </section>

    <section class="section muted">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">Scenes</p>
          <h2>适用场景</h2>
          <p>从精装入场前置查验到交付前质量确认，服务围绕项目真实节点展开。</p>
        </div>
        <div class="scenario-grid">
          <article v-for="item in scenarios" :key="item">
            <i class="el-icon-location-outline"></i>
            <h3>{{ item }}</h3>
          </article>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">Workflow</p>
          <h2>服务流程</h2>
          <p>流程以现场可执行、资料可追溯、整改可复核为核心，不用营销化承诺替代实际交付。</p>
        </div>
        <ol class="process-list">
          <li v-for="(step, index) in process" :key="step.title">
            <span>{{ index + 1 }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section class="section deliver-section">
      <div class="section-inner deliver-layout">
        <div>
          <p class="eyebrow">Deliverables</p>
          <h2>交付成果</h2>
          <p>根据项目要求输出分户、楼栋、标段、专项或阶段性维度的查验资料。</p>
        </div>
        <ul class="deliver-list">
          <li v-for="item in deliverables" :key="item">
            <i class="el-icon-document-checked"></i>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
      <div class="section-inner proof-row">
        <figure v-for="item in proofAssets" :key="item.title">
          <img :src="item.image" :alt="item.title + '对公服务样张示意图'" />
          <figcaption>{{ item.title }}（示意展示）</figcaption>
        </figure>
      </div>
    </section>

    <section class="section muted">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">Project References</p>
          <h2>项目经验 / 案例展示</h2>
          <p>以下内容来自现有站点资料。后续建议补充可公开的真实项目照片、报告样张和客户授权资料。</p>
        </div>
        <div class="case-grid">
          <article v-for="item in cases" :key="item.title" class="case-card">
            <img :src="item.image" :alt="item.title + '服务场景示意图'" />
            <div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.service }}</p>
              <router-link to="/cases">查看案例</router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">FAQ</p>
          <h2>对公服务常见问题</h2>
        </div>
        <div class="faq-list">
          <details v-for="item in faqs" :key="item.question">
            <summary>{{ item.question }}</summary>
            <p>{{ item.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <section class="section cta-section">
      <div class="section-inner cta-inner">
        <div>
          <h2>需要制定精装入场或交付前查验方案？</h2>
          <p>提供项目城市、楼栋范围、预计户数、当前节点和报告要求后，我们会进一步沟通服务范围与进场安排。</p>
        </div>
        <router-link class="btn" :to="{ path: '/', hash: '#lead-form' }">咨询项目方案</router-link>
      </div>
    </section>

    <div class="beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">{{ company.icp }}</a>
      <a :href="company.publicSecurityUrl" target="_blank" rel="noopener">{{ company.publicSecurityRecord }}</a>
    </div>
  </div>
</template>

<script>
import { companyInfo, homeFaqs, proofAssets, projectCases, publicServices, serviceProcessDetails } from "../data/services";
import {
  absoluteUrl,
  breadcrumbSchema,
  localBusinessSchema,
  organizationSchema,
  setSeo,
  setStructuredData
} from "../utils/seo";

export default {
  name: "ServiceCenter",
  data() {
    return {
      company: companyInfo,
      services: publicServices,
      process: serviceProcessDetails,
      faqs: homeFaqs,
      proofAssets: proofAssets,
      cases: projectCases,
      highlights: [
        { title: "精装入场前置", desc: "在批量装修前发现移交条件与基层质量问题。" },
        { title: "过程持续跟踪", desc: "按阶段巡检、记录、汇总并推动整改。" },
        { title: "分户批量管理", desc: "按户、楼栋、专业类别输出问题资料。" },
        { title: "整改复查闭环", desc: "复核整改状态，减少问题遗漏和反复沟通。" }
      ],
      customers: ["开发商", "建设单位", "物业单位", "建筑企业", "精装修项目", "工程项目", "住宅交付项目", "项目管理方"],
      scenarios: ["精装修单位进场前移交查验", "施工过程阶段性质量巡检", "住宅集中交付前分户查验", "一房一验资料建立", "业主开放日前预验收", "物业接管前公共区域查验", "整改完成后第三方复查", "渗漏、空鼓、门窗、水电等专项检测"],
      deliverables: ["分户问题清单", "问题照片记录", "阶段性问题汇总", "楼栋 / 标段汇总表", "专项检测记录", "整改建议", "复查状态记录", "检测报告或查验报告"]
    };
  },
  mounted() {
    setSeo({
      title: "对公工程查验服务｜精装入场查验｜过程质量巡检｜江苏道鼎升工程检测",
      description:
        "江苏道鼎升工程检测有限公司对公工程查验服务面向开发商、建设单位、物业单位和住宅交付项目，覆盖精装入场查验、过程质量巡检、分户查验、一房一验、交付前预验收、整改复查和工程质量检测。",
      path: "/services",
      image: "/img/hero-project-inspection.webp"
    });
    const serviceSchemas = this.services.map(function(service) {
      return {
        "@type": "Service",
        "@id": absoluteUrl(service.path + "#service"),
        name: service.shortTitle,
        serviceType: service.serviceType,
        description: service.summary,
        provider: {
          "@id": absoluteUrl("/#organization")
        },
        areaServed: companyInfo.serviceArea,
        url: absoluteUrl(service.path)
      };
    });
    setStructuredData([
      organizationSchema(),
      localBusinessSchema(),
      ...serviceSchemas,
      breadcrumbSchema([
        { name: "首页", path: "/" },
        { name: "对公工程查验服务", path: "/services" }
      ])
    ]);
  }
};
</script>

<style lang="scss" scoped>
.services-page {
  background: #f4f6f8;
  color: #1f2d3d;
}

.page-hero {
  min-height: 470px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.page-hero img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.page-hero-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(7, 25, 40, 0.94), rgba(12, 58, 83, 0.72), rgba(12, 58, 83, 0.3));
}

.page-hero-content,
.section-inner {
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
}

.page-hero-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.breadcrumb {
  display: flex;
  gap: 8px;
  margin-bottom: 26px;
  color: rgba(255, 255, 255, 0.78);
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
}

.eyebrow {
  color: #c8872a;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.page-hero h1 {
  max-width: 860px;
  font-size: 44px;
  line-height: 1.24;
  margin: 0 0 18px;
}

.page-hero p {
  max-width: 920px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 17px;
  line-height: 1.9;
}

.btn {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  border-radius: 4px;
  background: #c8872a;
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  margin-top: 18px;
}

.section {
  padding: 76px 0;
}

.muted {
  background: #fff;
}

.intro-layout,
.two-column,
.deliver-layout {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 40px;
  align-items: start;
}

.section-heading {
  max-width: 820px;
  margin-bottom: 32px;
}

h2 {
  color: #102d42;
  font-size: 32px;
  line-height: 1.35;
  margin: 0 0 14px;
}

.section-heading p,
.intro-layout p,
.two-column p,
.deliver-layout p,
.service-card p,
.case-card p,
.faq-list p,
.cta-section p {
  color: #5e6b76;
  line-height: 1.8;
}

.highlight-grid,
.tag-grid,
.scenario-grid,
.deliver-list,
.case-grid,
.proof-row {
  display: grid;
  gap: 14px;
}

.highlight-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.highlight-grid article,
.tag-grid span,
.scenario-grid article,
.deliver-list li,
.service-card,
.case-card,
.faq-list details,
.proof-row figure {
  border: 1px solid #dfe6eb;
  border-radius: 8px;
  background: #fff;
}

.highlight-grid article {
  padding: 22px;
}

.highlight-grid strong {
  display: block;
  color: #0c4a6e;
  font-size: 20px;
  margin-bottom: 8px;
}

.highlight-grid span {
  color: #5e6b76;
  line-height: 1.7;
}

.service-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.service-card {
  overflow: hidden;
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
}

.service-card img {
  width: 100%;
  height: 100%;
  min-height: 260px;
  display: block;
  object-fit: cover;
}

.service-card div {
  padding: 26px;
}

.service-card span {
  display: inline-flex;
  margin-bottom: 12px;
  padding: 6px 10px;
  border-radius: 4px;
  background: #eef5f8;
  color: #0c4a6e;
  font-size: 13px;
  font-weight: 700;
}

.service-card h3,
.scenario-grid h3,
.case-card h3 {
  color: #102d42;
  font-size: 21px;
  line-height: 1.45;
  margin: 0 0 12px;
}

.service-card a,
.case-card a {
  color: #0c4a6e;
  font-weight: 700;
  text-decoration: none;
}

.tag-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tag-grid span {
  padding: 16px;
  color: #0c4a6e;
  font-weight: 700;
}

.scenario-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.scenario-grid article {
  min-height: 146px;
  padding: 24px;
}

.scenario-grid i {
  color: #c8872a;
  font-size: 24px;
  margin-bottom: 16px;
}

.process-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.process-list li {
  min-height: 176px;
  padding: 22px;
  background: #102d42;
  border-radius: 8px;
}

.process-list span {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  background: #c8872a;
  color: #fff;
  font-weight: 700;
}

.process-list h3 {
  color: #fff;
  margin: 16px 0 8px;
  font-size: 18px;
}

.process-list p {
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
}

.deliver-section {
  background: #eef3f7;
}

.deliver-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  list-style: none;
  padding: 0;
  margin: 0;
}

.deliver-list li {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 16px;
}

.deliver-list i {
  color: #c8872a;
}

.proof-row {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 30px;
}

.proof-row figure {
  margin: 0;
  overflow: hidden;
}

.proof-row img {
  width: 100%;
  height: 210px;
  display: block;
  object-fit: cover;
}

.proof-row figcaption {
  padding: 14px 16px;
  color: #5e6b76;
}

.case-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.case-card {
  overflow: hidden;
}

.case-card img {
  width: 100%;
  height: 210px;
  object-fit: cover;
  display: block;
}

.case-card div {
  padding: 22px;
}

.faq-list {
  display: grid;
  gap: 12px;
}

.faq-list details {
  padding: 0 22px;
}

.faq-list summary {
  cursor: pointer;
  list-style: none;
  color: #102d42;
  font-weight: 700;
  padding: 20px 0;
}

.faq-list summary::-webkit-details-marker {
  display: none;
}

.faq-list p {
  border-top: 1px solid #edf2f1;
  margin: 0;
  padding: 16px 0 20px;
}

.cta-section {
  background: #102d42;
}

.cta-inner {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
}

.cta-inner h2,
.cta-inner p {
  color: #fff;
}

@media (max-width: 1080px) {
  .service-grid,
  .intro-layout,
  .two-column,
  .deliver-layout,
  .tag-grid,
  .scenario-grid,
  .deliver-list,
  .process-list,
  .proof-row,
  .case-grid {
    grid-template-columns: 1fr;
  }

  .service-card {
    grid-template-columns: 240px minmax(0, 1fr);
  }

  .cta-inner {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .page-hero-content,
  .section-inner {
    width: min(100% - 28px, 1180px);
  }

  .page-hero {
    min-height: auto;
    padding: 70px 0;
  }

  .page-hero h1 {
    font-size: 30px;
  }

  .section {
    padding: 56px 0;
  }

  h2 {
    font-size: 26px;
  }

  .service-card {
    grid-template-columns: 1fr;
  }

  .service-card img {
    min-height: 220px;
  }
}
</style>
